// ----------------------------------------------
// Submittal Registry Types (aka Submittal Registry Items)
// ----------------------------------------------

import { ISimpleProject } from '@app/pages/webapp/projects/projects.interface';
import { PersistedFile } from '@app/framework/interfaces/file.type';

export enum SUBMITTAL_REGISTRY_STATUS {
  NO_STATUS = 'no_status', // this is null on the backend, but an actual value is needed for the frontend
  FOR_APPROVAL = 'for_approval',
  REVISE_RESUBMIT = 'revise_resubmit',
  APPROVED = 'approved',
  APPROVED_NOTED = 'approved_noted',
  REJECTED = 'rejected',
  VOID = 'void',
  NOT_RECEIVED = 'not_received',
  // PRE_PACKAGE = 'pre_package',
  RECEIVED = 'received',
  NOT_REVIEWED = 'not_reviewed',
  NOT_SENT = 'not_sent',
}

export const SUBMITTAL_REGISTRY_STATUS_OPTIONS = [
  { label: 'No Status', value: SUBMITTAL_REGISTRY_STATUS.NO_STATUS },
  { label: 'Not Received', value: SUBMITTAL_REGISTRY_STATUS.NOT_RECEIVED },
  { label: 'Received', value: SUBMITTAL_REGISTRY_STATUS.RECEIVED },
  { label: 'For Approval', value: SUBMITTAL_REGISTRY_STATUS.FOR_APPROVAL },
  { label: 'Approved', value: SUBMITTAL_REGISTRY_STATUS.APPROVED },
  { label: 'Approved Noted', value: SUBMITTAL_REGISTRY_STATUS.APPROVED_NOTED },
  { label: 'Revise Resubmit', value: SUBMITTAL_REGISTRY_STATUS.REVISE_RESUBMIT },
  { label: 'Rejected', value: SUBMITTAL_REGISTRY_STATUS.REJECTED },
  { label: 'Void', value: SUBMITTAL_REGISTRY_STATUS.VOID },
  { label: 'Not Sent', value: SUBMITTAL_REGISTRY_STATUS.NOT_SENT },
  { label: 'Not Reviewed', value: SUBMITTAL_REGISTRY_STATUS.NOT_REVIEWED },
  // { label: 'Pre Package', value: SUBMITTAL_REGISTRY_STATUS.PRE_PACKAGE },
];

export type SubmittalRegistrySortingKeys =
  | 'custom_id'
  | 'spec_section'
  | 'submittal_type'
  | 'owner'
  | 'received_submittals_at'
  | 'received_approval_at'
  | 'sent_to_owner_at'
  | 'package'
  | 'status';

export type SubmittalRegistryFilterParams = {
  page?: number;
  perPage?: number;
  status?: SUBMITTAL_REGISTRY_STATUS[];
  contractor?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export type SubmittalRegistryPayloadDto = {
  project_id: number;
  owner_id: number;
  custom_id: string;
  spec_section: string;
  spec_description: string;
  item_description: string;
  note?: string;
  status?: SUBMITTAL_REGISTRY_STATUS;
};

export type SubmittalRegistry = {
  id: number;
  project_id: number;
  custom_id: string;
  spec_description: string;
  spec_section: string;
  item_description: string;
  owner: {
    id: number;
    name: string;
    company_name: string;
  } | null;
  author_id: number;
  note: string;
  // received_date: string | null;
  // approved_date: string | null;
  version: number;
  package: SubmittalRegistryPackage | null;
  status: SUBMITTAL_REGISTRY_STATUS;
  requested_submittals_at: string;
  received_submittals_at: string;
  requested_approval_at: string;
  received_approval_at: string;
  sent_to_owner_at: string;
  voided_at: string;
};

export type SubmittalRegistryPartialUpdateDto = {
  note?: string;
  status?: SUBMITTAL_REGISTRY_STATUS;
  is_locked?: boolean;
};

export type SubmittalRegistryPackage = {
  id: number;
  package_name: string;
};

// ----------------------------------------------
// Submittal Package Types (aka Submittal Log)
// ----------------------------------------------

export enum SUBMITTAL_PACKAGE_STATUS {
  OPEN = 'open',
  CLOSED = 'closed',
  VOID = 'void',
}

export type SubmittalLogSortingKeys =
  | 'package_name'
  | 'package_title'
  | 'sent_date'
  | 'due_date'
  | 'late_days'
  | 'answered_date'
  | 'submittal_manager'
  | 'submittal_approver'
  | 'status';

export type SubmittalPackageFilterParams = {
  page?: number;
  status?: SUBMITTAL_PACKAGE_STATUS[];
  submittal_type?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export const SUBMITTAL_PACKAGE_STATUS_OPTIONS = [
  { label: 'Open', value: SUBMITTAL_PACKAGE_STATUS.OPEN },
  { label: 'Closed', value: SUBMITTAL_PACKAGE_STATUS.CLOSED },
  { label: 'Void', value: SUBMITTAL_PACKAGE_STATUS.VOID },
];

export type SubmittalPackageParticipant = {
  user_id: number;
  name: string;
  company_name: string;
};

export type SubmittalPackageUser = {
  id: number;
  name: string;
  company_name: string;
  action_required: boolean;
};

export type SubmittalPackageListingItem = {
  id: number;
  spec_section: string;
  spec_version: number;
  package_version: number;
  title: string;
  package_name: string;
  entries_approved_count: number;
  entries_count: number;
  role: SubmittalPackageRole;

  due_date: string;
  late_days: number;
  requested_submittals_at: string | null;
  requested_approval_at: string | null;
  received_approval_at: string | null;
  received_submittals_at: string | null;
  sent_to_owner_at: string | null;

  package_manager: SubmittalPackageUser;
  package_approver: SubmittalPackageUser;
  status: SUBMITTAL_PACKAGE_STATUS;
};

export type SubmittalPackageSentFor = 'Approval' | 'Submittals' | 'Owner';
export type SubmittalPackageDocumentCategory = 'reviewed' | 'source' | 'submitted';
export type SubmittalPackageRole = 'approver' | 'manager' | 'owner' | 'participant';

export type SubmittalMessagingGroup = {
  id: number;
  name: string;
};

export type SubmittalFile = File & {
  doc_type: string;
  id: number;
  name: string;
  s3_path: string;
  size: number;
  type: string;
};

export type AvailableSubmittalPackageVersion = {
  id: number;
  package_version: number;
};

export type SubmittalPackageResp = {
  id: number;
  spec_section: string;
  spec_version: number;
  package_version: number;
  package_versions: AvailableSubmittalPackageVersion[];
  title: string;
  package_name: string;
  role: SubmittalPackageRole;
  action_required: boolean;

  due_date: string;
  requested_submittals_at: string | null;
  requested_approval_at: string | null;
  sent_to_owner_at: string | null;
  received_approval_at: string | null;
  received_submittals_at: string | null;

  package_manager: SubmittalPackageUser;
  package_approver: SubmittalPackageUser;
  entries: SubmittalRegistry[];
  status: SUBMITTAL_PACKAGE_STATUS;
  participants: SubmittalPackageParticipant[];
  project: ISimpleProject;
  messaging_group: SubmittalMessagingGroup;
  files: {
    reviewed: SubmittalFile[];
    source: SubmittalFile[];
    submitted: SubmittalFile[];
  };
};

export type SubmittalPackage = SubmittalPackageResp & {
  sent_for: SubmittalPackageSentFor;
};

export type SubmittalPackageTableData = SubmittalPackageListingItem & {
  late_days: number | null;
  package_manager: SubmittalPackageUser & {
    action_required: boolean;
  };
  package_approver: SubmittalPackageUser & {
    action_required: boolean;
  };
};

export type SubmittalPackageCreateUpdateReq = {
  id?: number;
  project_id: number;
  spec_section: string;
  title: string;
  due_date: string;
  package_manager: {
    id: number;
  };
  package_approver: {
    id: number;
  };
  participants: { user_id: number }[];
  entries: { id: number }[];
};

export type SubmittalPackagePatchPayloadDto = {
  void?: boolean;
  send_for_approval?: boolean;
  request_submittals?: boolean;
  status: SUBMITTAL_PACKAGE_STATUS;
};

export type SubmittalPackageOptions =
  | 'cancel_request'
  | 'request_submittals'
  | 'send_for_approval'
  | 'cancel_approval'
  | 'mark_as_complete' // approver marks as complete
  | 'mark_as_ready' // owner marks as ready
  | 'send_to_owner'
  | 'cancel_send_to_owner'
  | 'create_revision'
  | 'edit_package'
  | 'download_cover'
  | 'void'
  | 'close'
  | 'reopen'
  | 'delete';

export type SubmittalPackageModelRegistryItem = {
  id: number | null;
  spec_section: string | null;
  spec_description: string | null;
  item_description: string | null;
};

export type SubmittalPackageModel = {
  id?: number;
  project_id: number | null;
  spec_section: string;
  package_version: number;
  package_title: string;
  submittals_due_date: string;
  package_manager_id: number | null;
  package_approver_id: number | null;
  participants: {
    id: number;
    name: string;
  }[];
  registry_items: SubmittalPackageModelRegistryItem[];
};

export type SubmittalRegistryBulkAssignDataDto = {
  project_id: number;
  entry_ids: number[]; // registry item IDs
  owner_id?: number;
  package_id?: null | number;
};

export type SubmittalPackageDocumentCategoryOption = {
  label: string;
  value: SubmittalPackageDocumentCategory;
  canBeEdited: boolean;
};

// ----------------------------------------------
// Transmittal Types
// ----------------------------------------------

export type TransmittalLogRegistryEntry = {
  id: number;
  status: SUBMITTAL_REGISTRY_STATUS;
  custom_id: string;
  spec_section: string;
  item_description: string;
  spec_description: string;
};

export type TransmittalLogAction =
  | 'request_submittals'
  | 'request_submittals_undo'
  | 'received_submittals'
  | 'request_approval'
  | 'request_approval_undo'
  | 'received_approval'
  | 'sent_to_owner'
  | 'sent_to_owner_undo';

export type TransmittalLogResp = {
  id: number;
  sender: SubmittalPackageUser;
  receiver: SubmittalPackageUser;
  action: TransmittalLogAction;
  entries: TransmittalLogRegistryEntry[];
  action_date: string;
};

export type TransmittalLog = TransmittalLogResp & {
  actionLabel: string;
  cover: PersistedFile;
};
